import { Component, Event, EventEmitter, h, Method, Prop, State, Watch } from '@stencil/core';
import { FormRenderProps, FormValues } from '../../stencil-form/declarations';
import InputField from '../../stencil-form/InputField';
import { Unsubscribe } from '@stencil/redux';
import { store } from '../../../redux/store';
import { dataFormFieldDescription, dataFormFieldLabel, dataFormFieldOptionValueKey, trans } from '../../../utils/trans';
import { StepItem, Translations } from '../../../redux/types';

import SelectField from '../../stencil-form/SelectField';
import { getDataFormInputValues, markStepAsLoaded } from '../../../redux/actions';
import { RootState } from '../../../redux/reducers';
import { debounce, getCountriesOptions } from '../../../utils/utils';

@Component({
  tag: 'rentware-custom-data-form-step',
  styleUrl: 'rentware-custom-data-form-step.sass',
  shadow: false,
})
export class RentwareCustomDataFormStep {
  @Event({ eventName: 'rentwareSubmitDataForm' }) submitDataForm: EventEmitter;
  @Prop({ mutable: true }) translations: Translations;
  @Prop({ mutable: true }) locale: string;
  @Prop({ mutable: true }) defaultLanguage: string;
  @Prop() onFormValid;
  @Prop() currentStep: StepItem;
  @Prop() errors;
  @Prop() isSavingDataForm: boolean;
  @Prop() saveDataFormValues;


  @State() dataForm: any;
  @State() valuesLoaded: boolean = false;

  unsubscribe!: Unsubscribe;
  getDataFormInputValues!: typeof getDataFormInputValues;
  markStepAsLoaded!: typeof markStepAsLoaded;
  formRef: any;
  templateRef;

  debounceSaveAndReload = debounce(() => this.saveValuesAndReload(), 500)

  @Method()
  async goToFirstError() {
    this.goToError();
  }

  @Method()
  async saveData(saveFunction: (formData: FormData) => void) {
    const values = await this.formRef.submitForm();
    const formData = new FormData();

    for (const [key, value] of Object.entries(values)) {
      formData.append(key, value as string | Blob);
    }

    const dataFormIds = this.dataForm.dataForms.map((item) => item.id);
    formData.append('dataFormIds', dataFormIds.join(','));

    saveFunction(formData);
  }

  @Method()
  async validate(onSuccess: (values: { [key: string]: string }) => void, onFail: (values: any) => void) {
    const values = await this.formRef.submitForm();
    const isValid = await this.formRef.isFormValid();
    if (isValid && onSuccess) {
      onSuccess(values);
      return true;
    } else {
      if (!!onFail) onFail(values);
      return false;
    }
  }

  componentDidRender() {
    this.goToError();
  }

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      return {
        translations: state.system.translations,
        locale: state.app.locale,
        defaultLanguage: state.system.defaultLanguage,
      };
    });

    store.mapDispatchToProps(this, {
      markStepAsLoaded,
      getDataFormInputValues,
    });
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  componentDidLoad() {
    this.loadData();
    this.markStepAsLoaded(this.currentStep.id);
  }

  loadData() {
    this.getDataFormInputValues(
      this.currentStep.config.dataFormId,
      resp => {
        this.dataForm = resp;
        this.valuesLoaded = true;
      },
      () => {
        this.valuesLoaded = true;
      },
    );
  }

  goToError() {
    this.templateRef?.goToFirstError();
  }

  renderDataFormFields = (props: FormRenderProps<FormValues>) => {
    const renderedDataForms =  this.dataForm.dataForms.map((dataForm) => {
      const dataFormId = dataForm.id;

      return dataForm.fields.map(field => {
        const fieldName = dataFormId + ':' + field.id;
        const isFileInput = field.type === 'file';
        const isParagraph = field.type === 'paragraph';

        let label = trans(dataFormFieldLabel(field.id), this.locale, this.translations);

        let fieldClass = 'InputField ' + field.dataFormId;
        if (isFileInput || isParagraph) {
          fieldClass += ' FullWidth';
        }

        const validate: any = {};
        if (field.isRequired === true) {
          validate.required = true;
          label += '*';
        }

        if (field.type === 'select') {
          const options = [];
          if (field.dataFormFieldOptions.length > 0) {
            field.dataFormFieldOptions.forEach(elem => {
              const option = { value: '', label: '' };
              option.value = elem.value;
              option.label = trans(dataFormFieldOptionValueKey(elem.id), this.locale, this.translations);
              options.push(option);
            });
          }
          return (
            <div class={fieldClass}>
              <rentware-spacer size='M' />
              <SelectField
                {...props}
                 name={fieldName}
                 label={label}
                 options={options}
                 validate={validate}
                 scrollToClick={true}
              />
            </div>
          );
        }

        //TODO This is very specific to the dataForm year option: min and max are hardcoded by PF
        if (field.type === 'year') {
          return (
            <div class={fieldClass}>
              <rentware-spacer size='M' />
              <InputField
                {...props}
                type='number'
                name={fieldName}
                min='1900'
                max={(new Date().getFullYear() + 1).toString()}
                label={label}
                validate={validate}
                placeholder={trans('dataForms.placeholderYearInput', this.locale, this.translations)}
              />
            </div>
          );
        }

        if (field.type === 'country') {
          return (
            <div class={fieldClass}>
              <rentware-spacer size='M' />
              <SelectField {...props} name={fieldName} label={label}
                           options={getCountriesOptions(this.locale, this.translations)} validate={validate}
                           scrollToClick={true} />
            </div>
          );
        }

        if (field.type === 'paragraph') {
          return (
            <div class={fieldClass}>
              <rentware-spacer size='M' />
              <div innerHTML={trans(dataFormFieldDescription(field.id), this.locale, this.translations)}/>
            </div>
          );
        }

        if (field.type === 'addressAutoComplete') {
          return (
            <div class={fieldClass}>
              <rentware-spacer size='M' />
              <rtr-address-autocomplete
                formProps={props}
                label={label}
                name={fieldName}
                validate={validate}
              />
            </div>
          );
        }

        return (
          <div class={fieldClass}>
            <rentware-spacer size='M' />
            <InputField
              {...props}
              type={field.type}
              name={fieldName}
              label={label}
              validate={validate}
              locale={this.locale}
              disabled={field.readOnly}
              value={field.readOnly ? this.dataForm.values?.[fieldName] : undefined}
            />
          </div>
        );
      });
    });

    const finalFields = []
    renderedDataForms.forEach(renderedField => {
      finalFields.push(renderedField, <div style={{width: '100%'}}/>);
    })

    return finalFields;
  };

  saveValuesAndReload() {
    this.saveDataFormValues(false, () => this.loadData());
  }

  @Watch('dataForm')
  watchDataFormChange(newDataForm: any, oldDataForm: any) {
    if (!newDataForm || !oldDataForm) {
      return;
    }

    const newDataFormIds = newDataForm.dataForms.map((item) => item.id)
    const oldDataFormIds = oldDataForm.dataForms.map((item) => item.id)
    if (newDataFormIds.length === oldDataFormIds.length) {
      if (!newDataFormIds.every((item, index) => item === oldDataFormIds[index])) {
        this.saveValuesAndReload();
      }
    } else {
      this.saveValuesAndReload();
    }
  }

  handleValueChange= () => {
    this.debounceSaveAndReload()
  }

  render() {
    // means not loaded yet
    if (!this.currentStep.config.dataFormId || !this.valuesLoaded) {
      return null;
    }

    return (
      <rentware-checkout-data-form-template
        dataForm={this.dataForm}
        locale={this.locale}
        translations={this.translations}
        submitDataForm={this.submitDataForm}
        renderDataFormFields={this.renderDataFormFields}
        dataFormFormRef={e => (this.formRef = e)}
        onFormValid={this.onFormValid}
        ref={el => (this.templateRef = el)}
        currentStep={this.currentStep}
        errors={this.errors}
        isSavingDataForm={this.isSavingDataForm}
        onValueChange={this.handleValueChange}
      />
    );
  }
}
